import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Greeting } from './Greeting';
import { Ads } from './Ads';
import { Dashboard } from './Dashboard';
import { CampaignFeed } from './CampaignFeed';
import { Login } from './Login';
import { Register } from './Register';
import { CreateCampaign } from './CreateCampaign';
import { CampaignDetail } from './CampaignDetail';
import { Profile } from './Profile';
import { NetworkTree } from './NetworkTree';
import { AuthProvider } from '../contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { useMobile } from '../hooks/useMobile';
import PasskeyLogin from './PasskeyLogin';
import PasskeyRegister from './PasskeyRegister';
import PasskeyQRAuth from './PasskeyQRAuth';
import UpgradeToPasskey from './UpgradeToPasskey';
import PasskeySetup from './PasskeySetup';
import GoogleCallback from './GoogleCallback';

type MainContentProps = {
  children: React.ReactNode;
};

const MainContent: React.FC<MainContentProps> = ({ children }) => {
  const location = useLocation();
  const isMobile = useMobile();
  const showAds = location.pathname !== '/network';
  const showGreeting =
    location.pathname === '/' || location.pathname === '/dashboard';

  return (
    <div className="layout-outer">
      <div className="layout-inner">
        <Sidebar />

        <div className={`layout-main ${showAds ? 'layout-main--with-ads' : ''} ${isMobile ? 'layout-main--mobile-fixed-nav' : ''}`}>
          {showGreeting && <Greeting />}
          <div className="layout-scroll">{children}</div>
        </div>

        {showAds && (
          <div className="layout-ads">
            <Ads />
          </div>
        )}
      </div>
    </div>
  );
};

export const Layout: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <MainContent>
                <Dashboard />
              </MainContent>
            }
          />
          <Route
            path="/campaigns"
            element={
              <MainContent>
                <CampaignFeed />
              </MainContent>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/passkey-login" element={<PasskeyLogin />} />
          <Route path="/passkey-register" element={<PasskeyRegister />} />
          <Route path="/passkey-qr-auth" element={<PasskeyQRAuth />} />
          <Route path="/upgrade-to-passkey" element={<UpgradeToPasskey />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route
            path="/passkey-setup"
            element={
              <ProtectedRoute>
                <MainContent>
                  <PasskeySetup />
                </MainContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:id"
            element={
              <MainContent>
                <CampaignDetail />
              </MainContent>
            }
          />
          <Route
            path="/create-campaign"
            element={
              <ProtectedRoute>
                <MainContent>
                  <CreateCampaign />
                </MainContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <MainContent>
                  <Profile />
                </MainContent>
              </ProtectedRoute>
            }
          />
          <Route
          path="/network"
          element={
            <ProtectedRoute>
              <NetworkLayout />
            </ProtectedRoute>
          }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

const NetworkLayout: React.FC = () => {
  const isMobile = useMobile();
  
  return (
    <div className="layout-outer">
      <div className="layout-inner">
        <Sidebar />
        <div className={`layout-main layout-main--network ${isMobile ? 'layout-main--mobile-fixed-nav' : ''}`}>
          <div className="layout-scroll">
            <NetworkTree />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
